import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PlusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { getProjectsAsync, patchProjectInList } from "../../../slices/project";
import {
  ProjectsTable,
  getProjectColumns,
  ProjectSyncBadge,
} from "../../../components/dashboard/projects/projects-table";
import ProjectCreateGuideDialog from "../../../components/dashboard/projects/ProjectCreateGuideDialog";
import { debounce } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  updateUrlWithException,
  ItemStatus,
  DeliveryAddressTitle,
  dateToString,
} from "../../../components/Utils";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getDepartmentsAsync } from "../../../slices/department";
import { getCustomers } from "../../../slices/customer";
import { getContactsAsync } from "../../../slices/customer/contact";
import { getUsersAsync } from "../../../slices/user";
import { fetchTagDefinitions } from "../../../api/tagDefinitions";
import { updateProject } from "../../../api/project";
import { CaseTagSelector } from "../../../components/dashboard/cases/CaseTagSelector";
import {
  PencilSquareIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { BulkBar } from "../../../components/utils/BulkBar";
import { TableToolbar } from "../../../components/utils/TableToolbar";
import { PMDialog } from "../../../components/utils/PMDialog";
import { useTableView } from "../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../components/utils/PMTableView/RightDrawer";
import { useProjectAccountingAccess } from "../../../routes/roleOnly";
import {
  buildProjectsIndexParams,
  projectsListViewFromFilters,
  PROJECT_LIST_VIEWS,
} from "../../../utils/projectsIndexParams";

export const ProjectList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const itemState = useSelector((store) => {
    return store.projects;
  });

  const departmentState = useSelector((store) => {
    return store.departments;
  });

  const customerState = useSelector((store) => {
    return store.customers;
  });

  const contactState = useSelector((store) => {
    return store.contacts;
  });

  const usersState = useSelector((store) => store.users);

  const { isLoading } = itemState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [drawerProject, setDrawerProject] = useState(null);
  const [projectTagDefinitions, setProjectTagDefinitions] = useState([]);

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  // Initialize filters from URL (so links/filters are restorable) or default
  const getInitialFiltersFromUrl = () => {
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        return JSON.parse(filtersParam).filter((f) => f?.name !== "active");
      } catch (e) {
        console.error("Error parsing filters from URL:", e);
      }
    }
    return [];
  };

  const getInitialActiveView = () => {
    const statusView = searchParams.get("statusView");
    if (statusView && PROJECT_LIST_VIEWS.includes(statusView)) {
      return statusView;
    }
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        return projectsListViewFromFilters(JSON.parse(filtersParam));
      } catch (e) {
        console.error("Error parsing filters from URL:", e);
      }
    }
    return "active";
  };

  const [activeView, setActiveView] = useState(() => getInitialActiveView());

  const [controller, setController] = useState({
    filters: getInitialFiltersFromUrl(),
    per_page: 10,
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "created_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    offer_scope: searchParams.get("offer_scope") || "projects",
  });

  // Persist all controller params (including filters) to the URL
  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        offer_scope: controller.offer_scope,
        statusView: activeView,
        filters:
          controller.filters && controller.filters.length > 0
            ? JSON.stringify(controller.filters)
            : null,
      },
      "filters"
    );
  }, [controller, activeView]);

  // Column definitions + table-view state (columns, grouping, density, views)
  const canProjectAccounting = useProjectAccountingAccess();
  const handleProjectTagsChange = useCallback(
    async (item, tagIds) => {
      const response = await updateProject({ id: item.id, tag_ids: tagIds });
      if (!response?.id) {
        toast.error(t("project_list.status_tags.save_error"));
        return;
      }
      dispatch(patchProjectInList(response));
      setDrawerProject((current) => (current?.id === response.id ? response : current));
    },
    [dispatch, t]
  );
  const handleSyncUpdated = useCallback(
    (projectId, tripletexSync) => {
      if (!projectId || !tripletexSync) return;
      dispatch(patchProjectInList({ id: projectId, tripletex_sync: tripletexSync }));
      setDrawerProject((current) =>
        current?.id === projectId ? { ...current, tripletex_sync: tripletexSync } : current
      );
    },
    [dispatch]
  );
  const columns = useMemo(
    () =>
      getProjectColumns(t, {
        showFinancialColumns: canProjectAccounting,
        tagDefinitions: projectTagDefinitions,
        onTagsChange: handleProjectTagsChange,
        onSyncUpdated: handleSyncUpdated,
      }),
    [t, canProjectAccounting, projectTagDefinitions, handleProjectTagsChange, handleSyncUpdated]
  );
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "projects",
    ensureVisibleKeys: ["status_tags"],
    columns: columnMeta,
    getSnapshot: () => ({
      filters: controller.filters,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      view: activeView,
      offer_scope: controller.offer_scope,
    }),
    applySnapshot: (snap) => {
      setActiveView(snap.view || "all");
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || [],
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
        offer_scope: snap.offer_scope || c.offer_scope,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleQueryChange = (newQuery) => {
    setController({
      ...controller,
      page: 1,
      query: newQuery,
    });
  };

  const projectsQueryParams = useMemo(
    () => buildProjectsIndexParams(controller, { listView: activeView }),
    [controller, activeView]
  );

  const handleFiltersChange = (filters) => {
    const backendFilters = [];

    // Handle customer filters
    if (filters.customers && filters.customers.length > 0) {
      filters.customers.forEach((customerId) => {
        backendFilters.push({
          name: "customer",
          value: customerId,
        });
      });
    }

    // Handle contact filters
    if (filters.contacts && filters.contacts.length > 0) {
      filters.contacts.forEach((contactId) => {
        backendFilters.push({
          name: "contact",
          value: contactId,
        });
      });
    }

    // Handle department filters
    if (filters.departments && filters.departments.length > 0) {
      filters.departments.forEach((departmentId) => {
        backendFilters.push({
          name: "department",
          value: departmentId,
        });
      });
    }

    if (filters.users && filters.users.length > 0) {
      backendFilters.push({
        name: "project_manager_id",
        value: JSON.stringify(filters.users.map((id) => String(id))),
      });
    }

    if (filters.tags && filters.tags.length > 0) {
      backendFilters.push({
        name: "tag_ids",
        value: JSON.stringify(filters.tags.map((id) => parseInt(id, 10))),
      });
    }

    setController({
      ...controller,
      page: 1,
      query: filters.query || controller.query,
      filters: backendFilters,
    });
  };

  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const scopeOptions = [
    { value: "projects", label: t("project_list.scope.projects") },
    { value: "offers", label: t("project_list.scope.offers") },
    { value: "all", label: t("project_list.scope.all") },
  ];

  const filterPanelPlaceholders = useMemo(
    () => [{ key: "status", label: t("project_list.filter.placeholder_status") }],
    [t]
  );

  const secondaryFilterCount = useMemo(() => {
    return (controller.filters || []).filter((f) =>
      ["customer", "contact", "department", "project_manager_id", "tag_ids"].includes(f.name)
    ).length;
  }, [controller.filters]);

  const hasSecondaryFilters = secondaryFilterCount > 0;

  // View tabs for projects
  const projectViews = [
    { id: "active", name: t("Active") },
    { id: "inactive", name: t("Inactive") },
    { id: "all", name: t("All") },
  ];

  const handleViewChange = (viewId) => {
    setActiveView(viewId);
    setController((c) => ({ ...c, page: 1 }));
  };

  const listCount = itemState?.totalCount != null ? `${itemState.totalCount}` : null;

  // Infinite scroll: load the next page and append results
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(getProjectsAsync({ ...projectsQueryParams, page: nextPage, append: true }));
    setController((c) => ({ ...c, page: nextPage }));
  };

  const debouncedDispatchAction = debounce((params) => {
    dispatch(getProjectsAsync(params));
  }, 250);

  useEffect(() => {
    // Skip when handleLoadMore already dispatched for this controller update
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
  // Trigger the debounced dispatch action
    debouncedDispatchAction(projectsQueryParams);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [projectsQueryParams, dispatch]);

  // Fetch filter options on component mount
  useEffect(() => {
    // Fetch departments
    dispatch(
      getDepartmentsAsync({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "title",
      })
    );

    // Fetch customers
    dispatch(
      getCustomers({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "name",
      })
    );

    // Fetch contacts
    dispatch(
      getContactsAsync({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "name",
      })
    );

    dispatch(
      getUsersAsync({
        filters: [{ name: "employee_or_hired", value: true }],
        page: 1,
        per_page: 200,
        query: "",
        sort: "asc",
        sortBy: "name",
        view: "all",
        fields: "id,displayTitle,name",
      })
    );

    fetchTagDefinitions("project")
      .then((data) => setProjectTagDefinitions(Array.isArray(data) ? data : []))
      .catch(() => setProjectTagDefinitions([]));
  }, [dispatch]);

  const headerActions = [{}];

  // Convert API data to filter options format
  const customerOptions = customerState.data.map((customer) => ({
    value: customer.id.toString(),
    label: customer.displayTitle || customer.name,
  }));

  const contactOptions = contactState.data.map((contact) => ({
    value: contact.id.toString(),
    label: contact.displayTitle || contact.name,
  }));

  const departmentOptions = departmentState?.data?.map((department) => ({
    value: department.id.toString(),
    label: department.title,
  }));

  const managerOptions = (usersState?.data || []).map((user) => ({
    value: String(user.id),
    label: user.displayTitle || user.name || String(user.id),
  }));

  const tagOptions = projectTagDefinitions.map((tag) => ({
    value: String(tag.id),
    label: tag.name,
  }));

  // Convert backend filters to ModuleFilter format
  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query,
      customers: [],
      contacts: [],
      departments: [],
      users: [],
      tags: [],
    };

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        switch (filter.name) {
          case "customer":
            initialFilters.customers.push(filter.value);
            break;
          case "contact":
            initialFilters.contacts.push(filter.value);
            break;
          case "department":
            initialFilters.departments.push(filter.value);
            break;
          case "project_manager_id": {
            try {
              const ids = JSON.parse(filter.value);
              initialFilters.users = Array.isArray(ids) ? ids.map((id) => String(id)) : [];
            } catch (_err) {
              initialFilters.users = filter.value ? [String(filter.value)] : [];
            }
            break;
          }
          case "tag_ids": {
            try {
              const ids = JSON.parse(filter.value);
              initialFilters.tags = Array.isArray(ids) ? ids.map((id) => String(id)) : [];
            } catch (_err) {
              initialFilters.tags = filter.value ? [String(filter.value)] : [];
            }
            break;
          }
          default:
            break;
        }
      });
    }

    return initialFilters;
  };

  return (
    <>
      <DashboardHeader
        variant="list"
        title={t("Projects")}
        listCount={listCount}
        actions={headerActions}
        mainButton={{
          label: t("New"),
          action: () => setProjectDialogOpen(true),
          path: null,
          icon: PlusIcon,
        }}
      >
        <div className="p-3 space-y-2 md:p-5 md:space-y-4">
          {/* Mobile: compact search + collapsed filters */}
          <div className="space-y-2 md:hidden">
            <div className="flex items-center gap-2">
              <div className="relative flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200">
                <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <input
                  type="text"
                  value={controller.query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  placeholder={t("Search projects...")}
                  className="min-w-0 flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
                {controller.query ? (
                  <button
                    type="button"
                    onClick={() => handleQueryChange("")}
                    className="flex-shrink-0 text-slate-400 hover:text-slate-600"
                    aria-label={t("Clear")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className={`relative inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors focus:outline-none ${
                  hasSecondaryFilters
                    ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                }`}
                aria-label={t("Filter Projects")}
              >
                <FunnelIcon className="h-4 w-4" />
                {hasSecondaryFilters ? (
                  <span className="min-w-[18px] rounded-full bg-white/20 px-1.5 text-center text-[11px] tabular-nums">
                    {secondaryFilterCount}
                  </span>
                ) : null}
              </button>
            </div>

            {!mobileFiltersOpen && hasSecondaryFilters ? (
              <ModuleFilter
                chipsOnly
                disabled={isLoading}
                onFiltersChange={handleFiltersChange}
                initialFilters={getInitialFilters()}
                customerOptions={customerOptions}
                contactOptions={contactOptions}
                departmentOptions={departmentOptions}
                userOptions={managerOptions}
                usersFilterLabel={t("project_list.filter.placeholder_manager")}
                tagOptions={tagOptions}
                tagsFilterLabel={t("project_list.filter.placeholder_tags")}
              />
            ) : null}
          </div>

          {/* Desktop: full filter card */}
          <div className="hidden md:block">
            <ModuleFilter
              filterCard
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder="Search projects..."
              filterTitle={t("Filter Projects")}
              customerOptions={customerOptions}
              contactOptions={contactOptions}
              departmentOptions={departmentOptions}
              userOptions={managerOptions}
              usersFilterLabel={t("project_list.filter.placeholder_manager")}
              tagOptions={tagOptions}
              tagsFilterLabel={t("project_list.filter.placeholder_tags")}
              segmentedControl={{
                value: controller.offer_scope || "projects",
                onChange: (v) =>
                  setController((c) => ({
                    ...c,
                    page: 1,
                    offer_scope: v,
                  })),
                options: scopeOptions,
              }}
            />
          </div>

          {/* Table card */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)] md:rounded-[18px]">
            <TableToolbar
              views={projectViews}
              activeView={activeView}
              onViewChange={handleViewChange}
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <ProjectsTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={itemState}
              onItemsSelect={(items) => setSelectedItems(items)}
              density={tableView.density}
              hasMore={(itemState?.totalCount || 0) > (itemState?.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onRowClick={(project) => setDrawerProject(project)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerProject}
        onClose={() => setDrawerProject(null)}
        eyebrow={drawerProject ? `${t("Number")} ${drawerProject.project_number || ""}` : ""}
        title={drawerProject?.name || drawerProject?.displayTitle || t("Projects")}
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/projects/${drawerProject.id}`),
          },
        ]}
      >
        {drawerProject && (
          <>
            <DrawerSection title={t("Details")}>
              <DrawerProps
                items={[
                  { label: t("Status"), value: <ItemStatus item={drawerProject} /> },
                  ...(drawerProject?.tripletex_sync?.enabled
                    ? [
                        {
                          label: t("tripletex_sync.badge.column"),
                          value: (
                            <ProjectSyncBadge
                              item={drawerProject}
                              onSyncUpdated={handleSyncUpdated}
                            />
                          ),
                        },
                      ]
                    : []),
                  {
                    label: t("Type"),
                    value: drawerProject.is_offer
                      ? t("project_list.badge.offer")
                      : t("project"),
                  },
                  {
                    label: t("Customer"),
                    value:
                      drawerProject.customer?.displayTitle ||
                      drawerProject.customer?.name ||
                      "—",
                  },
                  {
                    label: t("Contact"),
                    value:
                      drawerProject.contact?.displayTitle ||
                      drawerProject.contact?.name ||
                      "—",
                  },
                  {
                    label: t("Address"),
                    value: drawerProject?.delivery_address?.id ? (
                      <DeliveryAddressTitle
                        deliveryAddress={drawerProject.delivery_address}
                      />
                    ) : (
                      "—"
                    ),
                  },
                  {
                    label: t("project_list.sub_of"),
                    value:
                      drawerProject.parent_project?.displayTitle ||
                      drawerProject.parent_project?.name ||
                      "—",
                  },
                  {
                    label: t("project_list.filter.placeholder_manager"),
                    value:
                      drawerProject.project_manager?.displayTitle ||
                      drawerProject.project_manager?.name ||
                      "—",
                  },
                  {
                    label: t("project_list.column.status_tags"),
                    value: (
                      <CaseTagSelector
                        value={(drawerProject.tag_definitions || []).map((tag) => tag.id)}
                        onChange={(ids) => handleProjectTagsChange(drawerProject, ids)}
                        showBadges
                        scopeModule="project"
                        definitions={projectTagDefinitions}
                        buttonLabel={t("project_list.column.status_tags")}
                      />
                    ),
                  },
                  {
                    label: t("Created"),
                    value: drawerProject.created_at
                      ? dateToString(drawerProject.created_at)
                      : "—",
                  },
                ]}
              />
            </DrawerSection>
            {drawerProject.description && (
              <DrawerSection title={t("Description")}>
                <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-slate-600">
                  {drawerProject.description}
                </p>
              </DrawerSection>
            )}
          </>
        )}
      </RightDrawer>

      {/* Floating bulk action bar */}
      <BulkBar
        count={selectedItems.length}
        onClear={() => setSelectedItems([])}
        actions={[
          {
            label: t("Edit"),
            icon: PencilSquareIcon,
            action: () => toast.error("Kommer snart"),
          },
          { separator: true },
          {
            label: t("Delete"),
            icon: TrashIcon,
            danger: true,
            action: () => toast.error("Kommer snart"),
          },
        ]}
      />

      <ProjectCreateGuideDialog
        open={projectDialogOpen}
        onClose={() => setProjectDialogOpen(false)}
        onRefresh={() => dispatch(getProjectsAsync(projectsQueryParams))}
      />

      <PMDialog
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title={t("Filter Projects")}
        size="md"
        dense
        closeOnOutsideClick
      >
        <div className="overflow-y-auto overscroll-contain px-3 pb-3 sm:px-4 sm:pb-4">
          <ModuleFilter
            panelMode
            hideSearch
            disabled={isLoading}
            onFiltersChange={handleFiltersChange}
            initialFilters={getInitialFilters()}
            customerOptions={customerOptions}
            contactOptions={contactOptions}
            departmentOptions={departmentOptions}
            userOptions={managerOptions}
            usersFilterLabel={t("project_list.filter.placeholder_manager")}
            tagOptions={tagOptions}
            tagsFilterLabel={t("project_list.filter.placeholder_tags")}
            panelPlaceholderSlots={filterPanelPlaceholders}
            segmentedControl={{
              value: controller.offer_scope || "projects",
              onChange: (v) =>
                setController((c) => ({
                  ...c,
                  page: 1,
                  offer_scope: v,
                })),
              options: scopeOptions,
            }}
          />
        </div>
      </PMDialog>
    </>
  );
};
